import type { AircraftType, AirportConfig, FlightLeg, ForcedLink, Millis, OpsEvent, Sector, Settings, Turn } from "./types";
import { HOUR, MIN, localHour, remainsOvernight } from "./time";

/** How long an aircraft with no departure is shown as parked, once split off by hand. */
export const STORAGE_HOURS = 48;
/** An aircraft put into storage stays on its stand until someone gives it a flight. */
export const STORED_DAYS = 30;

/** Best-known time for a leg: actual, then estimated, then scheduled. */
export function bestTime(leg: FlightLeg): Millis {
  return leg.actual ?? leg.estimated ?? leg.scheduled;
}

/** De-icing minutes assumed for a type that has no figure of its own. */
export const DEICE_DEFAULT_MIN = 15;

/**
 * How long a departure of this type holds its stand for de-icing: nothing while winter
 * operations are off, otherwise the type's own figure or the airport's default.
 */
export function deiceMinutes(type: AircraftType | undefined, settings: Settings): number {
  if (!settings.deicingEnabled) return 0;
  return type?.deiceMin ?? settings.deiceDefaultMin ?? DEICE_DEFAULT_MIN;
}

const SECTOR_RANK: Record<Sector, number> = { D: 0, T: 1, I: 2 };
export function widerSector(a: Sector, b: Sector): Sector {
  return SECTOR_RANK[a] >= SECTOR_RANK[b] ? a : b;
}

export function canonicalAirline(code: string, config: AirportConfig): string {
  return config.airlineAliases[code.toUpperCase()] ?? code.toUpperCase();
}

function flightNumeric(flightNumber: string): number | null {
  const m = flightNumber.match(/(\d+)\D*$/);
  return m ? Number(m[1]) : null;
}

/** Turn ids embed the leg ids ("T|<arr>|<dep>"); leg ids may contain ":" but never "|". */
export function turnId(arr: FlightLeg | null, dep: FlightLeg | null): string {
  return `T|${arr?.id ?? "-"}|${dep?.id ?? "-"}`;
}

/**
 * The by-registration rule walks a tail's legs in schedule order, so when it pairs an
 * arrival with the next departure of that tail there is nothing of the aircraft in between
 * in the feed. The airport's own feed publishes about a day ahead with registrations on
 * most WestJet legs, so a long gap between two legs of one tail is the aircraft sitting
 * here, not legs the feed missed: C-FWEW landed as WS3388 one evening and left as WS3389
 * at 18:10 the next day, 21 h 20 min later, and with an 18 h cap it was shown as a lone
 * inbound with a 45-minute stay while it sat on the stand all night. Thirty hours covers
 * any stay from one day's schedule to the next; beyond that the feed cannot vouch for
 * what happened in between.
 */
const MAX_LINK_BY_REG = 30 * HOUR;
const MAX_LINK_BY_NUMBER = 8 * HOUR;
/**
 * The last resort: same airline, nose to tail, no tail number to prove it either way.
 * Kept short on purpose. A Jazz or Air North aircraft turns inside an hour here, so a
 * pairing this weak is only believed when the times could not really mean anything else;
 * anything longer is left as two half-turns rather than glued together wrongly.
 */
const MAX_LINK_BY_GROUND_TIME = 3 * HOUR;

/**
 * A night stop: in on the evening bank, out on the morning one. Both flights are real and
 * in the data; they are simply too far apart for any of the rules above to pair them, and
 * the tail that would prove it is usually published only hours before departure. Left
 * unpaired the aircraft is planned as a short stay, the stand is handed to the morning,
 * and when the tail finally arrives the aircraft is found sitting on top of it.
 */
const RON_EVENING_FROM = 18;
const RON_MORNING_UNTIL = 11;
const RON_MIN = 4 * HOUR;
const RON_MAX = 16 * HOUR;

/**
 * Link arrivals and departures into turns (one aircraft visit each).
 *
 * 1. Same registration: an arrival followed by that aircraft's next departure.
 * 2. No registration: same airline, flight numbers one apart (WS3602 -> WS3603) or equal,
 *    departure after the arrival within a plausible ground time. Closest in time wins.
 * 3. Anything left is a one-sided turn with an assumed start or end.
 */
/**
 * Aircraft visits, from the flights of a day.
 *
 * `links` are pairings a planner made here: a tail swap, where the aircraft that arrived
 * as one flight leaves as another, or a split, where an arrival and its departure are no
 * longer the same aircraft. Those happen on the apron and do not always reach the feed,
 * so they are applied first and the rest is worked out around them.
 */
export function buildTurns(legs: FlightLeg[], config: AirportConfig, links: ForcedLink[] = []): Turn[] {
  const live = legs.filter((l) => l.status !== "cancelled");
  const used = new Set<string>();
  const pairs: Array<{ arr: FlightLeg | null; dep: FlightLeg | null; linkedBy: Turn["linkedBy"] }> = [];

  // 0. What a planner decided, which beats anything the feed implies.
  const byId = new Map(live.map((l) => [l.id, l]));
  for (const link of links) {
    const arr = link.arr ? byId.get(link.arr) : undefined;
    const dep = link.dep ? byId.get(link.dep) : undefined;
    // A pairing whose flights are gone (cancelled, or another day) is simply ignored.
    if (!arr && !dep) continue;
    if (arr && (arr.direction !== "ARR" || used.has(arr.id))) continue;
    if (dep && (dep.direction !== "DEP" || used.has(dep.id))) continue;
    pairs.push({ arr: arr ?? null, dep: dep ?? null, linkedBy: "planner" });
    if (arr) used.add(arr.id);
    if (dep) used.add(dep.id);
  }

  // 1. By registration.
  const byReg = new Map<string, FlightLeg[]>();
  for (const leg of live) {
    const reg = leg.registration?.trim().toUpperCase();
    if (!reg) continue;
    const list = byReg.get(reg) ?? [];
    list.push(leg);
    byReg.set(reg, list);
  }
  for (const list of byReg.values()) {
    // Pair on the schedule, not on estimates: a very late inbound is still the aircraft
    // that operates the next departure (which will be pushed back), not a new visit.
    list.sort((a, b) => a.scheduled - b.scheduled || (a.direction === "ARR" ? -1 : 1));
    for (let i = 0; i < list.length - 1; i++) {
      const a = list[i]!;
      const d = list[i + 1]!;
      if (used.has(a.id) || used.has(d.id)) continue;
      if (a.direction !== "ARR" || d.direction !== "DEP") continue;
      const gap = d.scheduled - a.scheduled;
      if (gap < 0 || gap > MAX_LINK_BY_REG) continue;
      pairs.push({ arr: a, dep: d, linkedBy: "registration" });
      used.add(a.id);
      used.add(d.id);
      i++;
    }
  }

  // 2. By flight number adjacency.
  const arrivals = live
    .filter((l) => l.direction === "ARR" && !used.has(l.id))
    .sort((a, b) => bestTime(a) - bestTime(b));
  const departures = live.filter((l) => l.direction === "DEP" && !used.has(l.id));
  for (const arr of arrivals) {
    const arrNum = flightNumeric(arr.flightNumber);
    if (arrNum === null) continue;
    const airline = canonicalAirline(arr.airline, config);
    const arrType = resolveKnownType(arr);
    const minGround = (config.aircraftTypes[arrType ?? ""]?.minTurnMin ?? 15) * MIN * 0.8;
    let best: FlightLeg | null = null;
    let bestGap = Infinity;
    for (const dep of departures) {
      if (used.has(dep.id)) continue;
      if (canonicalAirline(dep.airline, config) !== airline) continue;
      const depNum = flightNumeric(dep.flightNumber);
      if (depNum === null || Math.abs(depNum - arrNum) > 1) continue;
      if (arrType && dep.aircraftType && dep.aircraftType !== arrType) continue;
      // Two known tails that differ are two aircraft, whatever the flight numbers say: this
      // is how a tail swap made in the AODB reaches us, and pairing on the numbers undid it.
      const arrReg = arr.registration?.trim().toUpperCase();
      const depReg = dep.registration?.trim().toUpperCase();
      if (arrReg && depReg && arrReg !== depReg) continue;
      const gap = bestTime(dep) - bestTime(arr);
      if (gap < minGround || gap > MAX_LINK_BY_NUMBER) continue;
      if (gap < bestGap) {
        best = dep;
        bestGap = gap;
      }
    }
    if (best) {
      pairs.push({ arr, dep: best, linkedBy: "flight-number" });
      used.add(arr.id);
      used.add(best.id);
    }
  }

  // 2b. Same airline, nose to tail, nothing to contradict it.
  //
  // The airport feed often carries no registration, and an airline's inbound and outbound
  // flight numbers are frequently unrelated (AC8272 in, AC8394 out). Left alone, those
  // become two half-turns that each hold a stand for an assumed ground time and then
  // collide with each other. If the ground time between them is a realistic turn for the
  // aircraft, they are one visit.
  const stillArriving = live
    .filter((l) => l.direction === "ARR" && !used.has(l.id))
    .sort((a, b) => bestTime(a) - bestTime(b));
  for (const arr of stillArriving) {
    if (used.has(arr.id)) continue;
    const airline = canonicalAirline(arr.airline, config);
    const arrType = resolveKnownType(arr);
    const type = config.aircraftTypes[arrType ?? ""];
    const minGround = (type?.minTurnMin ?? 25) * MIN;
    let best: FlightLeg | null = null;
    let bestGap = Infinity;
    for (const dep of departures) {
      if (used.has(dep.id)) continue;
      if (canonicalAirline(dep.airline, config) !== airline) continue;
      // Two known tails that differ are two aircraft, whatever the times say.
      const arrReg = arr.registration?.trim().toUpperCase();
      const depReg = dep.registration?.trim().toUpperCase();
      if (arrReg && depReg && arrReg !== depReg) continue;
      const depType = resolveKnownType(dep);
      if (arrType && depType && depType !== arrType) continue;
      const gap = bestTime(dep) - bestTime(arr);
      if (gap < minGround || gap > MAX_LINK_BY_GROUND_TIME) continue;
      if (gap < bestGap) {
        best = dep;
        bestGap = gap;
      }
    }
    if (best) {
      pairs.push({ arr, dep: best, linkedBy: "ground-time" });
      used.add(arr.id);
      used.add(best.id);
    }
  }

  // 2c. Overnight: the evening bank in, the morning bank out.
  //
  // Taken in time order, so the first aircraft in takes the first flight out, which is how
  // a night stop is actually worked. Anything that contradicts it — two known tails that
  // differ, two known types that differ — is left alone, and a departure that already has
  // an arrival is never touched.
  const eveningArrivals = live
    .filter((l) => l.direction === "ARR" && !used.has(l.id) && localHour(bestTime(l), config.timezone) >= RON_EVENING_FROM)
    .sort((a, b) => bestTime(a) - bestTime(b));
  for (const arr of eveningArrivals) {
    if (used.has(arr.id)) continue;
    const airline = canonicalAirline(arr.airline, config);
    const arrType = resolveKnownType(arr);
    const arrReg = arr.registration?.trim().toUpperCase();
    let best: FlightLeg | null = null;
    for (const dep of departures) {
      if (used.has(dep.id)) continue;
      if (canonicalAirline(dep.airline, config) !== airline) continue;
      if (localHour(bestTime(dep), config.timezone) > RON_MORNING_UNTIL) continue;
      const depReg = dep.registration?.trim().toUpperCase();
      if (arrReg && depReg && arrReg !== depReg) continue;
      const depType = resolveKnownType(dep);
      if (arrType && depType && depType !== arrType) continue;
      const gap = bestTime(dep) - bestTime(arr);
      if (gap < RON_MIN || gap > RON_MAX) continue;
      /**
       * The return flight first: WS2215 in from Puerto Vallarta at 21:40 is the aircraft that
       * leaves as WS2214 to Puerto Vallarta at 09:15, not the first WestJet 737 out at 06:05.
       * The flight-number rule above looks only eight hours ahead, so an overnight return
       * reaches this one. Without a return flight, time order: the earliest morning departure
       * the airline has left.
       */
      const arrNum = flightNumeric(arr.flightNumber);
      const returns = (l: FlightLeg) => {
        const n = flightNumeric(l.flightNumber);
        return arrNum !== null && n !== null && Math.abs(n - arrNum) <= 1;
      };
      if (!best) best = dep;
      else if (returns(dep) && !returns(best)) best = dep;
      else if (returns(dep) === returns(best) && bestTime(dep) < bestTime(best)) best = dep;
    }
    if (best) {
      pairs.push({ arr, dep: best, linkedBy: "ground-time" });
      used.add(arr.id);
      used.add(best.id);
    }
  }

  // 3. Singles.
  for (const leg of live) {
    if (used.has(leg.id)) continue;
    pairs.push(
      leg.direction === "ARR"
        ? { arr: leg, dep: null, linkedBy: "none" }
        : { arr: null, dep: leg, linkedBy: "none" },
    );
  }

  const typeByReg = new Map<string, string>();
  for (const leg of live) {
    if (leg.registration && leg.aircraftType) typeByReg.set(leg.registration.toUpperCase(), leg.aircraftType);
  }

  return pairs
    .flatMap(({ arr, dep, linkedBy }) => splitLongTurn(makeTurn(arr, dep, linkedBy, config, typeByReg), config))
    .sort((a, b) => a.start - b.start || a.id.localeCompare(b.id));
}

/** Split a long ground time into an arrival stay and a departure stay (tow to parking between). */
export function splitLongTurn(t: Turn, config: AirportConfig): Turn[] {
  if (!t.arr || !t.dep) return [t];
  if (!config.settings.towSplitMin) return [t]; // 0 = aircraft stay parked on stand (RON)
  const limit = config.settings.towSplitMin * MIN;
  if (t.end - t.start <= limit) return [t];
  const type = t.aircraftType ? config.aircraftTypes[t.aircraftType] : undefined;
  const stay = (type?.targetTurnMin ?? 45) * MIN;
  const a: Turn = { ...t, id: `${t.id}#A`, dep: null, end: t.start + stay, segment: "arrival", pairedWith: `${t.id}#D` };
  // The departure stay is counted back from the departure itself: a de-icing hold
  // lengthens the end, and must not eat into the time the aircraft needs on the stand first.
  const d: Turn = { ...t, id: `${t.id}#D`, arr: null, start: bestTime(t.dep) - stay, segment: "departure", pairedWith: `${t.id}#A` };
  return [a, d];
}

export function guessType(airline: string, flightNumber: string, config: AirportConfig): string | null {
  const num = flightNumeric(flightNumber);
  if (num !== null) {
    for (const r of config.defaultTypeRules ?? []) {
      if (r.airline === airline && num >= r.from && num <= r.to) return r.type;
    }
  }
  return config.defaultTypeByAirline[airline] ?? null;
}

function resolveKnownType(leg: FlightLeg): string | null {
  return leg.aircraftType ? leg.aircraftType.toUpperCase() : null;
}

function makeTurn(
  arr: FlightLeg | null,
  dep: FlightLeg | null,
  linkedBy: Turn["linkedBy"],
  config: AirportConfig,
  typeByReg: Map<string, string>,
): Turn {
  const any = (arr ?? dep)!;
  const airline = canonicalAirline(any.airline, config);
  const registration = (arr?.registration ?? dep?.registration ?? null)?.toUpperCase() ?? null;

  let aircraftType = (arr?.aircraftType ?? dep?.aircraftType ?? null)?.toUpperCase() ?? null;
  let aircraftTypeInferred = aircraftType !== null && (arr?.aircraftType ? arr : dep)?.aircraftTypeFrom === "loads";
  if (!aircraftType && registration && typeByReg.has(registration)) {
    aircraftType = typeByReg.get(registration)!;
  }
  if (!aircraftType) {
    aircraftType = guessType(airline, any.flightNumber, config);
    aircraftTypeInferred = aircraftType !== null;
  }

  const type = aircraftType ? config.aircraftTypes[aircraftType] : undefined;
  const target = (type?.targetTurnMin ?? config.settings.defaultTurnMin) * MIN;

  // De-icing at YLW is done on the stand before pushback, so in winter the stand is not
  // free at the departure time: the aircraft is still on it being sprayed. The hold is
  // added after the departure time rather than taken out of the turn, and the pairing
  // rules above never see it.
  // Once the departure has an actual off-block time the spray is over and the aircraft
  // has gone: the hold is for a departure still to come.
  const deice = dep && dep.actual == null ? deiceMinutes(type, config.settings) : 0;

  let start: Millis;
  let end: Millis;
  let openStart = false;
  let openEnd = false;
  if (arr && dep) {
    start = bestTime(arr);
    end = Math.max(bestTime(dep) + deice * MIN, start + (type?.minTurnMin ?? 15) * MIN);
  } else if (arr) {
    start = bestTime(arr);
    // An aircraft a planner split off its departure is in storage: it sits on the apron
    // until someone gives it one, so it keeps holding its stand rather than disappearing
    // after a normal turn time.
    end = start + (arr.stored ? STORED_DAYS * 24 * HOUR : linkedBy === "planner" ? STORAGE_HOURS * HOUR : target);
    openEnd = true;
  } else {
    start = bestTime(dep!) - target;
    end = bestTime(dep!) + deice * MIN;
    openStart = true;
  }

  const sector = arr && dep ? widerSector(arr.sector, dep.sector) : any.sector;
  const ron = !!arr && !!dep && remainsOvernight(bestTime(arr), bestTime(dep), config.timezone);

  return {
    id: turnId(arr, dep),
    arr,
    dep,
    airline,
    aircraftType,
    aircraftTypeInferred,
    registration,
    sector,
    start,
    end,
    openStart,
    openEnd,
    linkedBy,
    ron,
    ...(deice > 0 ? { deiceMin: deice } : {}),
  };
}

/**
 * Turn ids change when a link changes (e.g. a registration arrives late).
 * Carry an existing stand assignment over to the new turn that contains the same legs.
 */
export function legIndex(turns: Turn[]): Map<string, string> {
  const idx = new Map<string, string>();
  for (const t of turns) {
    if (t.arr) idx.set(t.arr.id, t.id);
    if (t.dep) idx.set(t.dep.id, t.id);
  }
  return idx;
}

function eventMatches(t: Turn, e: OpsEvent): boolean {
  return (!!e.arrLegId && t.arr?.id === e.arrLegId) || (!!e.depLegId && t.dep?.id === e.depLegId);
}

/**
 * Apply planner actions to turns:
 * - mechanical hold: the aircraft stays on stand until `until` (the departure cannot leave earlier);
 * - extension: a planner holds the stand, desks or a belt for the flight past their usual
 *   end. A stand extension lengthens the stay itself, so the allocator and the conflict
 *   checks see an occupied stand; the others are carried on the turn for the resource
 *   planner (see resources.ts);
 * - tow: the visit is split at `at`; the aircraft continues on `toStand`.
 * Returns the adjusted turns and the stands fixed by tows (by segment id).
 */
export function applyOpsEvents(turns: Turn[], events: OpsEvent[]): { turns: Turn[]; fixed: Record<string, string> } {
  const fixed: Record<string, string> = {};
  if (events.length === 0) return { turns, fixed };
  const out: Turn[] = [];
  for (const original of turns) {
    let t = { ...original };
    const mine = events.filter((e) => e.kind !== "link" && eventMatches(original, e));
    for (const e of mine) {
      if (e.kind === "mech") {
        t = { ...t, end: Math.max(t.end, e.until), mech: { until: e.until, note: e.note }, openEnd: false };
      }
    }
    // After any hold, so a stand extension measures from where the stay really ends.
    for (const e of mine) {
      if (e.kind !== "extend") continue;
      // Only the leg the resource belongs to: desks and outbound belts work the
      // departure, the reclaim belt the arrival.
      if (e.resource === "belts" ? t.arr?.id !== e.arrLegId : e.resource !== "stand" && t.dep?.id !== e.depLegId) continue;
      const ext = { eventId: e.id, until: e.until, ...(e.note ? { note: e.note } : {}), ...(e.by ? { by: e.by } : {}) };
      if (e.resource === "stand") {
        t = { ...t, end: Math.max(t.end, e.until), openEnd: false, extensions: { ...t.extensions, stand: { ...ext, from: t.end } } };
      } else {
        t = { ...t, extensions: { ...t.extensions, [e.resource]: ext } };
      }
    }
    const tows = mine
      .filter((e): e is Extract<OpsEvent, { kind: "tow" }> => e.kind === "tow")
      .filter((e) => e.at > t.start && e.at < t.end)
      .sort((a, b) => a.at - b.at);
    if (tows.length === 0) {
      out.push(t);
      continue;
    }
    const name = t.arr && t.dep ? `${t.arr.flightNumber}/${t.dep.flightNumber}` : (t.arr ?? t.dep)!.flightNumber;
    const cuts = [t.start, ...tows.map((e) => e.at), t.end];
    for (let i = 0; i < cuts.length - 1; i++) {
      const first = i === 0;
      const last = i === cuts.length - 2;
      const id = first ? t.id : `${t.id}#T${i}`;
      const seg: Turn = {
        ...t,
        id,
        start: cuts[i]!,
        end: cuts[i + 1]!,
        arr: first ? t.arr : null,
        dep: last ? t.dep : null,
        segment: first ? t.segment : "towed",
        towedTo: first ? null : tows[i - 1]!.toStand,
        displayLabel: name,
        openStart: first ? t.openStart : false,
        openEnd: last ? t.openEnd : false,
      };
      if (!first) fixed[id] = tows[i - 1]!.toStand;
      out.push(seg);
    }
  }
  return { turns: out.sort((a, b) => a.start - b.start || a.id.localeCompare(b.id)), fixed };
}
